import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";
import locale from "@/utils/locale";
import type { SupportedLanguage } from "./i18n.types";
import { env } from "@/config/env";
// FCP/LCP 优化：i18n/index.ts 是落地页 entry 的同步依赖
// （landing-main 顶层 `import "@/i18n"` 触发 i18n.init）。
// 这里如果 import utils 整个文件 (2000+ 行 + axios + crypto-js + …)，
// landing entry chunk 就会重新被拖大 ~150KB。改为只引 storage-helpers
// 的 getObject/setObject —— 行为完全等价（utils.getObject/setObject 已经
// 改为对 storage-helpers 的薄代理，是同一份实现）。
import { getObject, setObject } from "@/utils/storage-helpers";
// FCP/A 优化：默认语言(en)文案直接打包进 JS 作为 init resources。
// 之前 en 靠运行时 fetch /locales/en/translation.json
// 且 MainShell 的 i18nReady 门会一直卡到 en 到位才渲染 —— 这是首屏
import enTranslation from "./locales/en/translation.json";

const ALL_KNOWN: SupportedLanguage[] = ["en", "pt", "es", "ar", "bn"];

/** 无 window._config 时从 VITE 解析，避免正式环境仅识别 en 导致 init 与 main 语言不一致 */
function parseEnvSupportedLanguages(): SupportedLanguage[] {
  const raw = env.SUPPORTED_LANGUAGES || "en,es";
  const out: SupportedLanguage[] = [];
  for (const part of raw.split(",")) {
    const id = part.trim() as SupportedLanguage;
    if (id && ALL_KNOWN.includes(id) && !out.includes(id)) {
      out.push(id);
    }
  }
  return out.length ? out : (["en"] as SupportedLanguage[]);
}

/**
 * 获取有效的语言列表（每次调用都读当前 _config，勿用模块加载时的快照）
 */
export const getValidLanguages = (): SupportedLanguage[] => {
  if (window._config?.languageList && window._config.languageList.length > 0) {
    return window._config.languageList.map(
      lang => lang.id as SupportedLanguage
    );
  }
  return parseEnvSupportedLanguages();
};

export const getDefaultLanguage = (): SupportedLanguage => {
  const valid = getValidLanguages();
  const cfgLang = window._config?.lang as SupportedLanguage | undefined;
  if (cfgLang && valid.includes(cfgLang)) return cfgLang;
  const fromEnv = (env.DEFAULT_LANGUAGE || "en") as SupportedLanguage;
  if (valid.includes(fromEnv)) return fromEnv;
  return valid[0] ?? "en";
};

/** @deprecated 使用 getValidLanguages() */
export const SUPPORTED_LANGUAGES: SupportedLanguage[] =
  parseEnvSupportedLanguages();

/** @deprecated 使用 getDefaultLanguage() */
export const DEFAULT_LANGUAGE: SupportedLanguage = (env.DEFAULT_LANGUAGE ||
  "en") as SupportedLanguage;

// main 里 initCountryAndLanguage 写入 localStorage 后，i18n 异步 init 仍会触发 languageChanged(en)，
// 在「可对齐完成」之前禁止把该事件写回存储，避免覆盖 es。
let canPersistLanguageToStorage = false;

export const getStoredLanguage = (): SupportedLanguage => {
  try {
    const valid = getValidLanguages();

    const storedLanguage = getObject(
      "currentLanguage"
    ) as SupportedLanguage;
    if (storedLanguage && valid.includes(storedLanguage)) {
      return storedLanguage;
    }

    if (
      window._config?.lang &&
      valid.includes(window._config.lang as SupportedLanguage)
    ) {
      return window._config.lang as SupportedLanguage;
    }

    if (
      window._config?.languageList &&
      window._config.languageList.length > 0
    ) {
      const firstLang = window._config.languageList[0].id;
      if (valid.includes(firstLang as SupportedLanguage)) {
        return firstLang as SupportedLanguage;
      }
    }

    const countryId = getObject("countryId") as string;
    if (countryId && window._config?.countryList) {
      const country = window._config.countryList.find(c => c.id === countryId);
      if (country?.lang && valid.includes(country.lang as SupportedLanguage)) {
        return country.lang as SupportedLanguage;
      }
    }

    return getDefaultLanguage();
  } catch {
    return getDefaultLanguage();
  }
};

export const setStoredLanguage = (lang: SupportedLanguage): void => {
  try {
    setObject("currentLanguage", lang);
  } catch (error) {
    console.error("Failed to store language:", error);
  }
};

const customMissingKeyHandler = (
  lng: readonly string[],
  ns: string,
  key: string,
  fallbackValue: string
): string => {
  const localeValue = locale.get(key);
  if (localeValue && localeValue !== key) {
    return localeValue;
  }
  return fallbackValue || key;
};

i18n.on("initialized", () => {
  void (async () => {
    try {
      // i18n init 用 en (见下面 init 调用),首屏命中 preload 缓存避免 key 闪烁。
      // 这里切回用户实际偏好。重新调用 getStoredLanguage() 而非闭包捕获,
      // 因为 _config 可能在 init 期间被 remote-head 注入,
      // 完整偏好链路: localStorage → window._config.lang → countryId.lang → defaultLanguage。
      const preferred = getStoredLanguage();
      const valid = getValidLanguages();
      if (preferred !== i18n.language && valid.includes(preferred)) {
        await i18n.changeLanguage(preferred);
      }
    } finally {
      canPersistLanguageToStorage = true;
    }
  })();
});

i18n.on("languageChanged", (lng: string) => {
  if (!canPersistLanguageToStorage) return;
  const valid = getValidLanguages();
  if (valid.includes(lng as SupportedLanguage)) {
    setStoredLanguage(lng as SupportedLanguage);
  }
});

setTimeout(() => {
  if (!canPersistLanguageToStorage) {
    canPersistLanguageToStorage = true;
  }
}, 8000);

// FCP 体验优化: 首屏渲染时若翻译未就绪, t() 会返回 key 字符串 (如 "home.welcome")。
// index.html 顶部固定 preload /locales/en/translation.json (读 <html lang="en">),
// 把 init 语言设为 en 可让 i18n 首次加载命中 preload 缓存, 把 key 闪烁窗口压缩到几乎为 0。
// init 完成后, 上面的 i18n.on("initialized") 自动切换到用户实际偏好 (es/pt/ar 等),
// 切换期间 canPersistLanguageToStorage=false, 不会覆盖用户语言偏好持久化。
// 兜底: 若 supportedLngs 不含 en (极端配置), 退回原行为直接用 stored language。
const _initLng: SupportedLanguage = getValidLanguages().includes(
  "en" as SupportedLanguage
)
  ? ("en" as SupportedLanguage)
  : getStoredLanguage();

i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    lng: _initLng,
    fallbackLng: getDefaultLanguage(),
    supportedLngs: getValidLanguages(),

    // 默认语言 en 打包进 bundle，init 同步就绪，省掉首屏 en 的网络 fetch + i18nReady 门。
    // partialBundledLanguages: true 下，未打包的语言仍由 HttpBackend 按需加载。
    resources: {
      en: { translation: enTranslation },
    },

    defaultNS: "translation",
    ns: ["translation"],

    /** ar 等语言可只维护部分 key，其余回退到 fallbackLng（通常为 en） */
    partialBundledLanguages: true,

    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
      requestOptions: {
        cache: "no-cache",
      },
    },

    interpolation: {
      escapeValue: false,
    },

    saveMissing: false,
    missingKeyHandler: customMissingKeyHandler,

    parseMissingKeyHandler: (key: string) => {
      const localeValue = locale.get(key);
      if (localeValue && localeValue !== key) {
        return localeValue;
      }
      return key;
    },

    // 性能优化（关闭 useSuspense：避免与路由懒加载、同步点击叠加时出现
    // 「A component suspended while responding to synchronous input」）
    react: {
      useSuspense: false,
      bindI18n: "languageChanged loaded",
      bindI18nStore: "added removed",
    },

    debug: env.I18N_DEBUG,

    load: "languageOnly",
  });

export default i18n;
